import React from "react";
import { ContainerProps, Header, Footer, LoadingV2, ActionButtons } from "@ucl/library";
import { faPlay } from "@fortawesome/free-solid-svg-icons";
import {
  SanctionSearchStatusTypes,
  SanctionMatchResultLevelTypes,
} from "@/types/SanctionsMatchReadDTO";
import { useSanctionsMatchFlyout } from "@/hooks/useSanctionsMatchFlyout";
import { useRunSanctionsMatch } from "@/hooks/useRunSanctionsMatch";
import { RunSanctionsMatchRequestDTO } from "@/types/RunSanctionsMatchRequestDTO";
import "./SanctionsMatchFlyout.scss";

export interface SanctionsMatchFlyoutProps extends ContainerProps {
  id?: string;
  financialApplicationId?: string;
  entityId?: string;
  entityType?: string;
}

export const SanctionsMatchFlyout: React.FC<SanctionsMatchFlyoutProps> = ({
  id,
  financialApplicationId,
  entityId,
  entityType,
  onExit,
}) => {
  const { sanctionsMatch, loading, error, refetch } = useSanctionsMatchFlyout(id);
  const {
    loading: runSanctionsLoading,
    error: runSanctionsError,
    runSanctionsMatch,
  } = useRunSanctionsMatch();

  const handleRunSanctionsMatch = async () => {
    if (!financialApplicationId || !entityId || !entityType) {
      console.error("Missing required parameters for running sanctions match");
      return;
    }

    try {
      const request: RunSanctionsMatchRequestDTO = {
        entityId: entityId,
        entityType: entityType,
      };

      await runSanctionsMatch(financialApplicationId, request);

      // Refetch the sanctions match data after running the match
      setTimeout(() => {
        refetch();
      }, 1000);
    } catch (err) {
      console.error("Failed to run sanctions match:", err);
    }
  };


  if (loading) {
    return (
      <div className="sanctions-match-flyout">
        <Header>Sanctions Match Details</Header>
        <div className="sanctions-match-flyout__content">
          <LoadingV2 />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sanctions-match-flyout">
        <Header>Sanctions Match Details</Header>
        <div className="sanctions-match-flyout__content">
          <div className="sanctions-match-flyout__error">
            <p>Error loading sanctions match data: {error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!sanctionsMatch) {
    return (
      <div className="sanctions-match-flyout">
        <Header>Sanctions Match Details</Header>
        <div className="sanctions-match-flyout__content">
          <p>No sanctions match data available.</p>
        </div>
      </div>
    );
  }

  const getSearchStatusLabel = (status: SanctionSearchStatusTypes): string => {
    switch (status) {
      case SanctionSearchStatusTypes.NotStarted:
        return "Not Started";
      case SanctionSearchStatusTypes.Completed:
        return "Completed";
      case SanctionSearchStatusTypes.Error:
        return "Error";
      default:
        return "Unknown";
    }
  };

  const getMatchResultLabel = (result: SanctionMatchResultLevelTypes): string => {
    switch (result) {
      case SanctionMatchResultLevelTypes.NoResults:
        return "No Results";
      case SanctionMatchResultLevelTypes.PotentialMatch:
        return "Potential Match";
      case SanctionMatchResultLevelTypes.ConfirmedMatch:
        return "Confirmed Match";
      default:
        return "Unknown";
    }
  };

  const getSearchStatusClass = (status: SanctionSearchStatusTypes): string => {
    switch (status) {
      case SanctionSearchStatusTypes.NotStarted:
        return "status-not-started";
      case SanctionSearchStatusTypes.Completed:
        return "status-completed";
      case SanctionSearchStatusTypes.Error:
        return "status-error";
      default:
        return "status-unknown";
    }
  };

  const getMatchResultClass = (result: SanctionMatchResultLevelTypes): string => {
    switch (result) {
      case SanctionMatchResultLevelTypes.NoResults:
        return "match-no-results";
      case SanctionMatchResultLevelTypes.PotentialMatch:
        return "match-potential";
      case SanctionMatchResultLevelTypes.ConfirmedMatch:
        return "match-confirmed";
      default:
        return "match-unknown";
    }
  };

  const getButtonText = (): string => {
    if (
      sanctionsMatch.searchStatus === SanctionSearchStatusTypes.Completed ||
      sanctionsMatch.searchStatus === SanctionSearchStatusTypes.Error
    ) {
      return "Re-run Sanctions Match";
    }
    return "Run Sanctions Match";
  };

  return (
    <div className="sanctions-match-flyout">
      <Header>Sanctions Match Details</Header>
      <div className="sanctions-match-flyout__content">
        {/* Basic Information Section */}
        <div className="sanctions-match-flyout__section">
          <h3 className="sanctions-match-flyout__section-title">Basic Information</h3>
          <div className="sanctions-match-flyout__fields">
            <div className="sanctions-match-flyout__field">
              <label className="sanctions-match-flyout__field-label">Screened Name</label>
              <div className="sanctions-match-flyout__field-value">
                {sanctionsMatch.screenedName}
              </div>
            </div>
            <div className="sanctions-match-flyout__field">
              <label className="sanctions-match-flyout__field-label">Search Status</label>
              <div className="sanctions-match-flyout__field-value">
                <span
                  className={`sanctions-match-flyout__status-badge ${getSearchStatusClass(
                    sanctionsMatch.searchStatus
                  )}`}
                >
                  {getSearchStatusLabel(sanctionsMatch.searchStatus)}
                </span>
              </div>
            </div>
            <div className="sanctions-match-flyout__field">
              <label className="sanctions-match-flyout__field-label">Match Result</label>
              <div className="sanctions-match-flyout__field-value">
                <span
                  className={`sanctions-match-flyout__match-badge ${getMatchResultClass(
                    sanctionsMatch.matchResult
                  )}`}
                >
                  {getMatchResultLabel(sanctionsMatch.matchResult)}
                </span>
              </div>
            </div>
            {sanctionsMatch.searchErrorReason && (
              <div className="sanctions-match-flyout__field sanctions-match-flyout__field--full-width">
                <label className="sanctions-match-flyout__field-label">Search Error Reason</label>
                <div className="sanctions-match-flyout__field-value sanctions-match-flyout__error-reason">
                  {sanctionsMatch.searchErrorReason}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* OpenSanctions Match Details Section */}
        {(sanctionsMatch.topOpenSanctionsMatch_EntityId ||
          sanctionsMatch.topOpenSanctionsMatch_EntityCaption ||
          sanctionsMatch.topOpenSanctionsMatch_Score !== undefined) && (
          <div className="sanctions-match-flyout__section">
            <h3 className="sanctions-match-flyout__section-title">Top OpenSanctions Match</h3>
            <div className="sanctions-match-flyout__fields">
              {sanctionsMatch.topOpenSanctionsMatch_EntityCaption && (
                <div className="sanctions-match-flyout__field sanctions-match-flyout__field--full-width">
                  <label className="sanctions-match-flyout__field-label">Entity Caption</label>
                  <div className="sanctions-match-flyout__field-value sanctions-match-flyout__entity-caption">
                    {sanctionsMatch.topOpenSanctionsMatch_EntityCaption}
                  </div>
                </div>
              )}
              {sanctionsMatch.topOpenSanctionsMatch_EntityId && (
                <div className="sanctions-match-flyout__field">
                  <label className="sanctions-match-flyout__field-label">Entity ID</label>
                  <div className="sanctions-match-flyout__field-value">
                    {sanctionsMatch.topOpenSanctionsMatch_EntityId}
                  </div>
                </div>
              )}
              {sanctionsMatch.topOpenSanctionsMatch_EntitySchema && (
                <div className="sanctions-match-flyout__field">
                  <label className="sanctions-match-flyout__field-label">Entity Schema</label>
                  <div className="sanctions-match-flyout__field-value">
                    {sanctionsMatch.topOpenSanctionsMatch_EntitySchema}
                  </div>
                </div>
              )}
              {sanctionsMatch.topOpenSanctionsMatch_Score !== undefined && (
                <div className="sanctions-match-flyout__field">
                  <label className="sanctions-match-flyout__field-label">Match Score</label>
                  <div className="sanctions-match-flyout__field-value">
                    <span className="sanctions-match-flyout__score">
                      {(sanctionsMatch.topOpenSanctionsMatch_Score * 100).toFixed(2)}%
                    </span>
                  </div>
                </div>
              )}
              {sanctionsMatch.topOpenSanctionsMatch_Match !== undefined && (
                <div className="sanctions-match-flyout__field">
                  <label className="sanctions-match-flyout__field-label">Is Match</label>
                  <div className="sanctions-match-flyout__field-value">
                    <span
                      className={`sanctions-match-flyout__boolean-badge ${
                        sanctionsMatch.topOpenSanctionsMatch_Match
                          ? "boolean-true"
                          : "boolean-false"
                      }`}
                    >
                      {sanctionsMatch.topOpenSanctionsMatch_Match ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sources Section */}
        {sanctionsMatch.topOpenSanctionsMatch_Sources_AsList &&
          sanctionsMatch.topOpenSanctionsMatch_Sources_AsList.length > 0 && (
            <div className="sanctions-match-flyout__section">
              <h3 className="sanctions-match-flyout__section-title">Sources</h3>
              <div className="sanctions-match-flyout__sources">
                {sanctionsMatch.topOpenSanctionsMatch_Sources_AsList.map(
                  (source: string, index: number) => (
                    <span key={index} className="sanctions-match-flyout__source-tag">
                      {source}
                    </span>
                  )
                )}
              </div>
            </div>
          )}
      </div>
      <Footer>
        <ActionButtons
          actions={[
            {
              text: "Cancel",
              onActionClick: () => {
                if (onExit) {
                  onExit();
                }
              },
              position: "left"
            },
            {
              text: getButtonText(),
              onActionClick: handleRunSanctionsMatch,
              isLoading: runSanctionsLoading,
              loadingProgressContent: "Running Sanctions Match...",
              loadingSuccessContent: "Running Sanctions Match...",
              disabled: runSanctionsLoading,
              iconProps: {
                icon: faPlay,
              },
              isPrimary: true,
              position: "right",
              isDisabled: () => {
                return !financialApplicationId || !entityId || !entityType;
              }
            }
          ]}
        />
        {runSanctionsError && (
          <div className="sanctions-match-flyout__error-message" style={{ marginTop: "10px", color: "red" }}>
            {runSanctionsError}
          </div>
        )}
      </Footer>
    </div>
  );
};

export default SanctionsMatchFlyout;
